import styled, { keyframes } from "styled-components";

import { mq } from "../../styles/canonical/breakpoints";

/* Animação por @keyframes CSS que tocam ao MONTAR (não framer-motion,
   não transição+rAF).

   Decisão 2026-05-30 (duas iterações):
   1) framer-motion AnimatePresence CONGELAVA o painel no meio do slide
      quando o pai re-renderizava durante a animação (DadosContext
      atualizando, ou ESC seguido de clicar outro card). Ficava preso em
      translateX(~647px) fora da tela + dialog fantasma montado.
   2) transição CSS + flip de classe via double-requestAnimationFrame
      RESOLVIA o congelamento, mas o setVisible(true) do rAF era STARVADO
      pelos re-renders pesados do DadosContext na main thread — o slide-in
      ficava parado em translateX(100%) (painel apagado, vazando por trás)
      por centenas de ms antes de assentar.

   Solução final: @keyframes com `forwards`. Animações CSS tocam sozinhas
   quando o elemento entra no DOM — sem timing em JS pra ser starvado. E
   styled-components reusa a MESMA classe em re-render do pai (props
   estáveis), então um painel já assentado nunca reinicia nem congela. */

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

/* fadeIn/fadeOut animam o escurecimento E o glass blur juntos, para o
   backdrop-filter entrar/sair suave (casado com o slide do Panel). Sem
   animar o filter, o blur "estala" instantâneo na entrada/saída. */
const fadeIn = keyframes`
  from {
    opacity: 0;
    backdrop-filter: blur(0px);
    -webkit-backdrop-filter: blur(0px);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }
  to {
    opacity: 0;
    backdrop-filter: blur(0px);
    -webkit-backdrop-filter: blur(0px);
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.42);
  /* Glass blur do conteúdo por trás do slide-over (vale p/ todos os
     slide-overs do app). O valor final é mantido após a animação (both). */
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  animation: ${({ $closing }) => ($closing ? fadeOut : fadeIn)} 0.2s ease both;
`;

export const Panel = styled.aside`
  position: relative;
  height: 100%;
  width: ${({ $width }) => $width || "min(90vw, 520px)"};
  max-width: 100vw;
  background: ${({ theme }) => theme.surface};
  border-left: 1px solid ${({ theme }) => theme.border};
  box-shadow: -16px 0 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${({ $closing }) => ($closing ? slideOut : slideIn)} 0.28s
    cubic-bezier(0.16, 1, 0.3, 1) both;
  will-change: transform;

  /* Auditoria mobile 2026-09-24 (S3): no celular o painel ocupa a tela
     inteira e todo controle de formulário vira alvo de toque de 44px.
     Campos com 16px vêm de responsive-additions.css (#root input...);
     aqui só a altura. Checkbox/radio ficam de fora — têm desenho próprio. */
  @media ${mq.mobile} {
    width: 100vw;
    border-left: 0;

    & input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]),
    & select {
      min-height: 44px;
    }
    & textarea {
      min-height: 88px;
    }
    & button:not([aria-hidden="true"]) {
      min-height: 44px;
    }
  }
`;

export const Header = styled.header`
  /* PWA iOS: o Overlay usa inset 0, entao o painel cobre a tela INTEIRA —
     inclusive a faixa da Dynamic Island. Sem esta soma, o titulo colide com
     o relogio e o X fica atras da bateria: o usuario nao consegue fechar o
     painel (relato com print, 2026-08-15). Fora do PWA a variavel e 0px. */
  padding: calc(16px + var(--safe-area-top, 0px)) 18px 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid var(--divider);
`;

export const HeaderText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/* Canônico .slideover__head .title — font-display 16px / -0.015em. */
export const Title = styled.h2`
  margin: 0;
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: var(--ink);
  line-height: 1.2;
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--ink-3);
  line-height: 1.4;
`;

export const CloseButton = styled.button`
  appearance: none;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textPrimary};
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.9rem;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }

  /* 35×30 no desktop; no celular vira o alvo de 44px que o polegar acerta
     (o Panel já força min-height, aqui a largura e o centro do ícone). */
  @media ${mq.mobile} {
    min-width: 44px;
    display: inline-grid;
    place-items: center;
    padding: 0;
  }
`;

export const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/* Canônico .slideover__foot — padding 12px 18px, fundo surface-2, topo divider. */
export const Footer = styled.footer`
  /* Idem no rodape: sem isto a barra de gestos do iPhone cobre a ultima
     linha de botoes (no relato, "Excluir definitivamente"). */
  padding: 12px 18px calc(12px + var(--safe-area-bottom, 0px));
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid var(--divider);
  background: var(--surface-2);

  /* Ações do rodapé dividem a largura no celular: dois botões de 44px que
     o polegar alcança sem mirar. */
  @media ${mq.mobile} {
    & > button {
      flex: 1 1 0;
      justify-content: center;
    }
  }
`;
